/**
 * Wire-mapping for the break-glass / emergency-role family (OIDCPAM Gate-6
 * Phase 5b — BreakGlassConsole.tsx + EmergencyRoles.tsx).
 *
 * AdminListEmergencyRolesResp.items and AdminListBreakGlassEventsResp.items
 * are typed `Array<Record<string, unknown>>` by the generator (a
 * polymorphic/opaque response schema — see gen/client/react/client.ts), but
 * the server actually writes the snake_case EmergencyRole / BreakGlassEvent
 * JSON (oidc/pam_breakglass.go: `max_ttl_seconds`, `resource_id`,
 * `activator_id`, `emergency_role_id`, `started_at`, `expires_at`,
 * `review_required`, `review_completed_at`, `reviewer_id`, `review_notes`,
 * ...) and the generated client's fromWire() converts every key to
 * camelCase before resolving (client.ts's schema-aware camelCase boundary
 * mapper). So at this boundary each item really is WireEmergencyRole /
 * WireBreakGlassEvent below, NOT the raw wire DTO.
 *
 * LATENT BUG FIXED BY THIS MAPPING: the pre-migration
 * BreakGlassConsole.tsx/EmergencyRoles.tsx cast these opaque items straight
 * to the hand-written SNAKE_CASE EmergencyRole/BreakGlassEvent types in
 * ./types (via `as unknown as`), which does not match the real camelCase
 * runtime shape. Every max_ttl_seconds/resource_id/activator_id/started_at/
 * expires_at/review_required/review_completed_at read off those objects was
 * `undefined` in production (EmergencyRoles rendered "max undefineds" and a
 * blank Resource line; BreakGlassConsole rendered blank Activator/Started/
 * Expires lines) — and because `overdue()` short-circuited on
 * `!ev.review_required` (always `undefined` => `!undefined` => `true` =>
 * return false unconditionally), the OVERDUE banner never rendered for a
 * genuinely overdue event. This mapping (camelCase runtime shape ->
 * @devnw/cyber-iam/iam/types camelCase props) fixes both as a side effect.
 */
import type {
  BreakGlassEvent as IamBreakGlassEvent,
  BreakGlassStatus,
  EmergencyRole as IamEmergencyRole,
} from '@devnw/cyber-iam/iam/types'

/** The real (camelCase) runtime shape of one AdminListEmergencyRolesResp item. */
export interface WireEmergencyRole {
  id: string
  name: string
  resourceId: string
  maxTtlSeconds: number
  permissions?: string[]
  scope?: string[]
  createdAt?: string
  updatedAt?: string
}

/** The real (camelCase) runtime shape of one AdminListBreakGlassEventsResp item. */
export interface WireBreakGlassEvent {
  id: string
  activatorId: string
  emergencyRoleId: string
  justification: string
  startedAt: string
  expiresAt: string
  reviewRequired: boolean
  reviewCompletedAt?: string
  reviewerId?: string
  reviewNotes?: string
  status: BreakGlassStatus
}

export function toIamEmergencyRole(w: WireEmergencyRole): IamEmergencyRole {
  return {
    id: w.id,
    name: w.name,
    resourceId: w.resourceId,
    maxTtlSeconds: w.maxTtlSeconds,
    permissions: w.permissions,
    scope: w.scope,
    createdAt: w.createdAt,
    updatedAt: w.updatedAt,
  }
}

/**
 * HOST-COMPUTED: expiry passed without a completed review. The library never
 * parses display strings — see `BreakGlassEvent.overdue`'s doc comment in
 * @devnw/cyber-iam/iam/types. Preserves the pre-migration page's `overdue()`
 * semantics exactly, now operating on the correctly-cased fields.
 */
export function computeBreakGlassOverdue(w: WireBreakGlassEvent): boolean {
  if (!w.reviewRequired || w.reviewCompletedAt) return false
  const exp = Date.parse(w.expiresAt)
  return !Number.isNaN(exp) && exp < Date.now()
}

export function toIamBreakGlassEvent(w: WireBreakGlassEvent): IamBreakGlassEvent {
  return {
    id: w.id,
    activatorId: w.activatorId,
    emergencyRoleId: w.emergencyRoleId,
    justification: w.justification,
    status: w.status,
    reviewRequired: w.reviewRequired,
    startedAt: w.startedAt,
    expiresAt: w.expiresAt,
    reviewCompletedAt: w.reviewCompletedAt,
    reviewerId: w.reviewerId,
    reviewNotes: w.reviewNotes,
    overdue: computeBreakGlassOverdue(w),
  }
}
